import { staticFormApiConnectorLegacy } from 'apiConnector/form/static';
import { withTabFreeze } from 'epic/utils/tabFreeze';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { type EnrichFormRequest, isProductSwitch } from 'models/forms/EnrichFormRequest';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { staticFormEnrichmentReceivedThunk } from 'state/static/staticForm.thunks';
import { getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';

export interface StaticFormEnrichmentRequestedEpicApi {
  enrichForm(request: EnrichFormRequest): Observable<StaticFormResult>;
}

export function staticFormEnrichmentRequestedEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    action$,
    state$,
    {
      oldHttpClient,
      frontLogger,
      selectors: { getFormEnrichedData, getFormPendingEnrichmentData, getStaticFormInfos },
    },
  ) =>
    action$.pipe(
      ofType('FORM_ENRICHMENT_REQUESTED'),
      withLatestFrom(state$),
      filter(([{ tabId }, state]) => getWorkspaceType(state, tabId) === 'static'),
      mergeMap(([{ tabId }, state]) => {
        const { negotiationId, version, sendWholeFormData } = getStaticFormInfos(state, tabId)!;

        const enrichedData = getFormEnrichedData(state, tabId);
        const pendingData = getFormPendingEnrichmentData(state, tabId);

        const request: EnrichFormRequest = {
          tabId,
          negotiationId,
          version,
          productType: (pendingData.ProductType || enrichedData.ProductType) as string,
          productSubType: (pendingData.ProductSubtype || enrichedData.ProductSubtype) as string,
          wrapper: (pendingData.Wrapper || enrichedData.Wrapper) as string,
          variationParameters: pendingData,
          rfq: sendWholeFormData ? enrichedData : undefined,
        };

        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        const result$ = staticFormApiConnectorLegacy(oldHttpClient)
          .enrichForm(request)
          .pipe(
            takeUntil(tabRemoved$),
            map(
              ({
                values,
                blocksMetadata,
                fieldsMetadata,
                version: newVersion,
                actions,
                quoteQuery,
                errors,
                productView: newProductView,
                mustForceAdvancedMode,
                allowedRequests,
              }) =>
                staticFormEnrichmentReceivedThunk(
                  tabId,
                  newProductView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                ),
            ),
            timeoutWith(timeoutInMillis, throwError(new Error('timeout')), appScheduler),
            catchError(error => {
              frontLogger.error(
                'Enrichment failure detected on static screen. Session linked to negotiation id {negotiationId} will be rollbacked {@FrontError}',
                negotiationId,
                error,
              );
              return of(commonFormActions.fetchFormEnrichmentFailed('static', tabId, error));
            }),
          );

        return isProductSwitch(request) ? withTabFreeze(result$, tabId, 'static') : result$;
      }),
    );
}
